/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import { getSubmitData, processHeaders, SCOPE_TYPES } from '../store/state/epics/util';
import Constants from '../constants';
import HeaderProcessor from '../service_broker/header-processor';
import CaseUtils from '../case/utils';

import type { CaseOptions } from './types';

import * as RestClient from './index';

class CaseAPIs {
  /**
   * Create the work object associated with the given caseTypeID
   * @param caseTypeID The work object to be created<br>
   * Example: "OPB1HW-SpaceTra-Work-RequestApproval"<br><br>
   * @param options - Optional, object containing extra infomation like startingFields and pageName
   * @returns response as promise
   */
  static createCase(caseTypeID: string, options: CaseOptions) {
    const {
      caseInfo,
      startingFields,
      flowType,
      viewType = SCOPE_TYPES.PAGE,
      pageName,
      context = Constants.APP.APP,
      debugInfo,
      headers,
      retrieveActionViewForNewObject = false
    } = options;

    // Support use case (Mashup) to allow the specification of some
    // initialContent used when creating the work.
    // For this, we are including this initial content in caseInfo.content
    // For standard OOTB behavior, it's ok for this to be empty/undefined
    // This initial content is then sent along in the body's "content"
    let initialContent = {};
    if (caseInfo && caseInfo.content) {
      initialContent = caseInfo.content;
    }
    const { content: externalObjectContent, pageInstructions: externalObjectPageInstructions } =
      CaseAPIs.getExternalObjectContent(caseTypeID, retrieveActionViewForNewObject, context);
    let requestPayload = {
      [CaseAPIs.getKeyBasedOnWorkType(caseTypeID)]: caseTypeID,
      content: {
        ...initialContent,
        ...startingFields,
        ...(CaseUtils.isExternalObject(caseTypeID) && !retrieveActionViewForNewObject && { ...externalObjectContent })
      }
    };
    requestPayload = {
      ...requestPayload,
      ...(CaseUtils.isExternalObject(caseTypeID) &&
        !retrieveActionViewForNewObject && {
          pageInstructions: externalObjectPageInstructions
        }),
      ...(!CaseUtils.isObjectCaseType(caseTypeID) && { processID: flowType })
    };

    const objectTypeViewPayload = CaseAPIs.getObjectTypeViewPayload(retrieveActionViewForNewObject, caseTypeID);
    return RestClient.invokeRestApi(
      CaseUtils.getRouteKeyForCreateWork(caseTypeID, retrieveActionViewForNewObject),
      {
        body: requestPayload,
        queryPayload: {
          ...objectTypeViewPayload,
          caseClassName: caseTypeID,
          viewType,
          params: {
            pageName
          }
        },
        headers: {
          ...headers,
          ...(debugInfo && { ...HeaderProcessor.addDebugHeaders(debugInfo) })
        }
      },
      context
    ).then((response) => ({
      response: response.data,
      requestPayload,
      headers: processHeaders(response.headers, caseTypeID, headers)
    }));
  }

  static getObjectTypeViewPayload = (retrieveActionViewForNewObject: boolean, caseTypeID: string) => {
    if (!retrieveActionViewForNewObject) {
      return {};
    }
    return { objectTypeID: caseTypeID, actionID: 'Create' };
  };

  static getExternalObjectContent = (caseTypeID: string, retrieveActionViewForNewObject: boolean, context: string) => {
    if (CaseUtils.isExternalObject(caseTypeID) && !retrieveActionViewForNewObject) {
      const submitData = getSubmitData(context, { isExternalObject: true });
      const { content, pageInstructions } = submitData.content;
      return { content, pageInstructions };
    }
    return {};
  };

  static getKeyBasedOnWorkType = (caseTypeID: string) =>
    CaseUtils.isObjectCaseType(caseTypeID) ? 'objectTypeID' : 'caseTypeID';
}

export default CaseAPIs;
